import { useCallback } from "react";

import { URL_HASH_REVEAL_SIDEBAR_PIN_KEY } from "constants/urlHashKeys";
import useUrlHashTrigger from "hooks/useUrlHashTrigger";

import useMainNavigationStore from "../stores/useMainNavigationStore";
import { MainNavigationItem } from "../types";

const findGroupIdContainingItem = (navigationItems: MainNavigationItem[], itemId: string) =>
  navigationItems.find(
    (navigationItem) =>
      "items" in navigationItem &&
      navigationItem.items.some((section) => section.items.some((item) => item.id === itemId))
  )?.id;

/**
 * Puts the sidebar into the state where an item's pin control is visible when a
 * `#revealSidebarPin=<itemId>` link asks for it — today the Userflow onboarding checklist,
 * which can't call into the app because its JavaScript action needs unsafe-eval that our CSP
 * denies. A pin only renders inside an expanded sidebar and an expanded group, and even then it
 * stays hover-hidden.
 */
const useMainNavigationExternalRevealPinTriggers = (navigationItems: MainNavigationItem[]) => {
  const revealPin = useCallback(
    (itemId: string) => {
      const groupId = findGroupIdContainingItem(navigationItems, itemId);

      if (!groupId) {
        return;
      }

      const store = useMainNavigationStore.getState();

      store.setIsSidebarExpanded(true);

      if (!store.expandedGroupIds.has(groupId)) {
        store.toggleGroupExpanded(groupId);
      }

      store.revealPin(itemId);
    },
    [navigationItems]
  );

  useUrlHashTrigger(URL_HASH_REVEAL_SIDEBAR_PIN_KEY, revealPin);
};

export default useMainNavigationExternalRevealPinTriggers;
